import { format } from 'date-fns'

import { softLaunchStageId } from '../../games/helpers/editGameHelpers'
import { GameVersion } from '../../games/types/GameAnalyses'
import { InternalGameDocument, InternalGameMutation } from '../../games/types/InternalGame'
import { ActiveFeatures, Benchmark, BenchmarkedGames, BenchmarkedGamesMap, InternalAnalysis, OverviewCategory } from '../types/InternalAnalysis'

export const maxBenchmarks = 3
export const newsCardDelayMs = 5000

type ProgressAnalysis = Pick<InternalAnalysis, 'progress' | 'powerScore'>

export const isPreviouslyFinalized = (analysis: Pick<InternalAnalysis, 'powerScore'>) => (analysis.powerScore ?? 0) > 0

// Legacy counts at most `featureCount` answers per category.
export const getAnswerCount = (category: OverviewCategory, analysis: ProgressAnalysis) => Math.min(analysis.progress?.[category.id] ?? 0, category.featureCount)

export const getActiveFeatureCount = (category: OverviewCategory, activeFeatures?: ActiveFeatures) => activeFeatures?.[category.id]?.length ?? 0

// "{answers} + {n} Future Features": features not active for the version, or the unanswered ones once finalized.
export const getFutureFeatureCount = (category: OverviewCategory, analysis: ProgressAnalysis, activeFeatures?: ActiveFeatures) =>
  category.featureCount - (isPreviouslyFinalized(analysis) ? getAnswerCount(category, analysis) : getActiveFeatureCount(category, activeFeatures))

export const isCategoryCompleted = (category: OverviewCategory, analysis: ProgressAnalysis) => getAnswerCount(category, analysis) === category.featureCount

// Legacy's progress: a category whose active features are all answered is complete, even if future features aren't.
export const getCategoryProgress = (category: OverviewCategory, analysis: ProgressAnalysis, activeFeatures?: ActiveFeatures) => {
  if (isPreviouslyFinalized(analysis)) return 100
  const answers = getAnswerCount(category, analysis)
  const inactive = category.featureCount - getActiveFeatureCount(category, activeFeatures)
  if (inactive > 0 && answers >= category.featureCount - inactive) return 100
  if (!category.featureCount) return 0
  return Math.ceil((answers / category.featureCount) * 100)
}

export const getTotalProgress = (categories: OverviewCategory[], analysis: ProgressAnalysis, activeFeatures?: ActiveFeatures) => {
  if (isPreviouslyFinalized(analysis)) return 100
  const total = categories.reduce(
    (sum, category) => ({
      featureCount: sum.featureCount + category.featureCount,
      answerCount: sum.answerCount + getAnswerCount(category, analysis),
      activeCount: sum.activeCount + getActiveFeatureCount(category, activeFeatures),
    }),
    { featureCount: 0, answerCount: 0, activeCount: 0 }
  )
  if (total.activeCount > 0 && total.answerCount >= total.activeCount) return 100
  if (!total.featureCount) return 0
  return Math.floor((total.answerCount / total.featureCount) * 100)
}

// Legacy also lets a finalized analysis recalculate whatever its progress.
export const canCalculatePowerScore = ({
  analysis,
  hasGame,
  versionCount,
  totalProgress,
}: {
  analysis: Pick<InternalAnalysis, 'gameVersion' | 'powerScore'>
  hasGame: boolean
  versionCount: number
  totalProgress: number
}) => {
  if (isPreviouslyFinalized(analysis)) return true
  if (!hasGame || totalProgress !== 100) return false
  return versionCount === 0 || !!analysis.gameVersion
}

export const toOverviewCategories = (categories: { id: string; name: string; type?: unknown; featureCount?: unknown; icon?: unknown }[]): OverviewCategory[] =>
  categories
    .filter(({ type, featureCount }) => type === 'user' && typeof featureCount === 'number' && featureCount > 0)
    .map(({ id, name, featureCount, icon }) => ({ id, name, featureCount: featureCount as number, icon: (icon as OverviewCategory['icon']) ?? null }))

const preferredMarketIsos = ['us', 'gb', 'au', 'ca', 'nz', 'sg', 'fi', 'se', 'ph']

export const getPreferredMarketIso = (storeLinks?: Record<string, string> | null) =>
  preferredMarketIsos.find((iso) => !!storeLinks?.[iso]) ?? Object.keys(storeLinks ?? {})[0]

// The market whose versions are listed: the soft launch market, a soft launch game's preferred store link, else the analysis'.
export const getVersionsMarketIso = (game: Pick<InternalGameDocument, 'softLaunchMarket' | 'stageId' | 'storeLinks'>, analysisMarketIso: string) => {
  if (game.softLaunchMarket) return game.softLaunchMarket
  if (game.stageId === softLaunchStageId) return getPreferredMarketIso(game.storeLinks) ?? analysisMarketIso
  return analysisMarketIso
}

// Newest first, each version once.
export const getVersionNames = (versions: GameVersion[]) => Array.from(new Set(versions.map(({ version }) => version)))

export const findVersion = (versions: GameVersion[], name?: string | null) => (name ? versions.find(({ version }) => version === name) : undefined)

export const formatReleaseDate = (timestamp?: number | null) => (timestamp ? format(timestamp, 'MMM d, yyyy') : '-')

const uniqueIds = (refs?: { id: string }[] | null) => Array.from(new Set((refs ?? []).map(({ id }) => id)))

// Legacy's overview saves send the stored game with stores and markets as ids and the changed field replaced.
export const toGameFieldSave = (stored: InternalGameDocument, changes: Partial<InternalGameMutation>): InternalGameMutation => ({
  ...stored,
  stores: uniqueIds(stored.stores),
  markets: uniqueIds(stored.markets),
  ...changes,
})

export const toBenchmarks = (entry?: BenchmarkedGames | null): Benchmark[] =>
  (entry?.gameIds ?? []).flatMap((gameId, index) => (entry?.marketIsos?.[index] ? [{ gameId, marketIso: entry.marketIsos[index] }] : []))

export const setBenchmarkedGames = (map: BenchmarkedGamesMap, gameId: string, benchmarks: Benchmark[]): BenchmarkedGamesMap => ({
  ...map,
  [gameId]: { gameIds: benchmarks.map((benchmark) => benchmark.gameId), marketIsos: benchmarks.map((benchmark) => benchmark.marketIso) },
})

export const sameBenchmarks = (a: Benchmark[], b: Benchmark[]) =>
  a.length === b.length && a.every((benchmark, index) => benchmark.gameId === b[index].gameId && benchmark.marketIso === b[index].marketIso)

type CalculationStamps = Pick<InternalAnalysis, 'finalizedAt' | 'modifiedAt' | 'ts'>
const calculationStampKeys: (keyof CalculationStamps)[] = ['finalizedAt', 'modifiedAt', 'ts']

// A first calculation stamps `finalizedAt`; whether a recalculation stamps it again isn't known, so any newer stamp
// on a finalized analysis counts.
export const isNewlyFinalized = (before: CalculationStamps, after: CalculationStamps) =>
  (after.finalizedAt ?? 0) > 0 && calculationStampKeys.some((key) => (after[key] ?? 0) > (before[key] ?? 0))
